import { useMutation } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { queryClient, getAuthToken } from "@/lib/queryClient";
import type { Chatbot } from "@shared/schema";

// A reusable per-bot enable/disable toggle. Used in both the chatbots
// list page and the dashboard's "Your Chatbots" cards. Each instance
// owns its own pending state so flipping one bot's switch never grays
// out the others.
//
// The card surrounding this toggle is typically wrapped in a <Link>, so
// we stop event propagation here — otherwise the click would also fire
// the link's navigation. preventDefault keeps the parent <a> from
// activating.
export function BotStatusToggle({ bot }: { bot: Chatbot }) {
  const { toast } = useToast();
  const mutation = useMutation({
    mutationFn: async (nextStatus: "active" | "inactive") => {
      const token = getAuthToken();
      const res = await fetch(`/api/chatbots/${bot.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const err: any = new Error(body.message || "Failed to update status");
        err.code = body.code;
        throw err;
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots"] });
    },
    onError: (err: any) => {
      toast({
        title: err.code === "BOT_LIMIT_REACHED" ? "Plan limit reached" : "Could not update status",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      className="flex items-center gap-2"
    >
      <Switch
        checked={bot.status === "active"}
        disabled={mutation.isPending}
        onCheckedChange={(checked) => mutation.mutate(checked ? "active" : "inactive")}
        data-testid={`switch-bot-status-${bot.id}`}
        aria-label={bot.status === "active" ? `Disable ${bot.name}` : `Enable ${bot.name}`}
      />
      <Badge
        variant={bot.status === "active" ? "default" : "secondary"}
        data-testid={`badge-bot-status-${bot.id}`}
      >
        {bot.status === "active" ? "Enabled" : "Disabled"}
      </Badge>
    </div>
  );
}
